<template>
    <div class="tinder">
        <transition name="fade" appear>
            <h2>
                <slot />
            </h2>
        </transition>
        <SlideInTransition
            v-if="loaded"
            from="top"
            translate-y="6vw"
            :stagger="0.3"
            ease="power2.out"
        >
            <div class="tinder-row">
                <div ref="controlWrapper" class="control-wrapper">
                    <EvaluationCard
                        v-if="finished"
                        :correct="correct"
                    >
                        <template #header>
                            <span v-if="correct">
                                <slot name="evaluation-correct">
                                    Genau!
                                </slot>
                            </span>
                            <span v-else>
                                <slot name="evaluation-incorrect" />
                            </span>
                        </template>
                        <slot name="evaluation-text" />
                    </EvaluationCard>
                </div>
                <div class="decision-wrapper" :class="left">
                    <div class="label">
                        Ja
                    </div>
                    <div class="image" :style="{paddingBottom, backgroundImage}" />
                </div>
                <div class="decision-wrapper" :class="right">
                    <div class="label">
                        Nein
                    </div>
                    <div class="image" :style="{paddingBottom, backgroundImage}" />
                </div>
            </div>
        </SlideInTransition>
    </div>
</template>

<script>
import ImageLoader from '@kids/lib/class/ImageLoader';
import SlideState from '@/mixins/SlideState';
import gsap from 'gsap';
import Draggable from 'gsap/Draggable';

gsap.registerPlugin(Draggable);

export default {
    name: 'Tinder',
    mixins: [SlideState],
    props: {
        image: {
            type: String,
            required: true
        },
        correctSide: {
            type: String,
            required: true
        }
    },
    data: () => ({
        finished: false,
        correct: false,
        loaded: false,
        paddingBottom: '',
        left: {
            logged: false,
            dropped: false,
            correct: false
        },
        right: {
            logged: false,
            dropped: false,
            correct: false
        }
    }),
    computed: {
        backgroundImage()
        {
            return `url(${this.image})`;
        }
    },
    mounted()
    {
        const imageLoader = new ImageLoader(this.image);
        imageLoader.once('loaded', () =>
        {
            const image = imageLoader.get(this.image);
            this.paddingBottom = `${(image.height / image.width) * 100}%`;
            this.loaded = true;
            this.$nextTick(() => this.activate(image));
        });
    },
    destroyed()
    {
        if (this._draggable)
        {
            this._draggable.kill();
            this._tweenRight.kill();
            this._tweenLeft.kill();
        }
    },
    methods: {
        activate(image)
        {
            image.classList.add('image');
            this.$refs.controlWrapper.appendChild(image);
            const state = this.getState();
            if (state)
            {
                gsap.set(image, {autoAlpha: 0});
                this[state].logged = true;
                this.evaluate(0);
                return;
            }
            this.createDraggable(image);
        },
        createDraggable(dragElement)
        {
            [this._draggable] = Draggable.create([dragElement], {
                type: 'x',
                bounds: 'body',
                onDrag: this.drag,
                onRelease: this.release
            });
            this._tweenRight = gsap.fromTo(
                dragElement,
                {opacity: 1, rotation: 0, scale: 1},
                {
                    opacity: 0.6,
                    rotation: 20,
                    scale: 0.7,
                    duration: 0.5,
                    paused: true,
                    ease: 'sine.in'
                }
            );
            this._tweenLeft = gsap.fromTo(
                dragElement,
                {opacity: 1, rotation: 0, scale: 1},
                {
                    opacity: 0.6,
                    rotation: -20,
                    scale: 0.7,
                    duration: 0.5,
                    paused: true,
                    ease: 'sine.in'
                }
            );
        },
        drag()
        {
            const draggable = this._draggable;
            if (draggable.x < 0)
            {
                const progress = draggable.x / draggable.minX;
                this.left.logged = progress > 0.5;
                this._tweenLeft.progress(progress);
            }
            else if (draggable.x > 0)
            {
                const progress = draggable.x / draggable.maxX;
                this.right.logged = progress > 0.5;
                this._tweenRight.progress(progress);
            }
            else
            {
                this._tweenRight.progress(0);
                this._tweenLeft.progress(0);
                this.left.logged = false;
                this.right.logged = false;
            }
        },
        release()
        {
            const draggable = this._draggable;

            draggable.disable();

            if (!this.right.logged && !this.left.logged)
            {
                let tween;

                if (draggable.x < 0)
                {
                    tween = this._tweenLeft;
                }
                else if (draggable.x > 0)
                {
                    tween = this._tweenRight;
                }
                else
                {
                    draggable.enable();
                    return;
                }

                tween.reverse();
                const duration = tween.time();

                gsap.to(draggable.target, {
                    x: 0,
                    duration,
                    onComplete: () =>
                    {
                        tween.reversed(false).pause();
                        draggable.enable();
                    }
                });
                return;
            }
            const duration = 0.4;

            gsap.to(draggable.target, {autoAlpha: 0, scale: 0.5, duration});
            this.evaluate(duration);
        },
        evaluate(duration)
        {
            if (!duration)
            {
                this.finished = true;
            }
            else
            {
                gsap.delayedCall(duration * 0.5, () => { this.finished = true; });
            }

            if (this.right.logged)
            {
                this.right.logged = false;
                this.right.dropped = true;
                if (this.correctSide === 'right')
                {
                    this.right.correct = true;
                }
                this.saveState('right');
            }
            else
            {
                this.left.logged = false;
                this.left.dropped = true;
                if (this.correctSide === 'left')
                {
                    this.left.correct = true;
                }
                this.saveState('left');
            }
            if (this.left.correct || this.right.correct)
            {
                this.correct = true;
            }
            if (duration)
            {
                this.$audioController.play(this.correct ? 'positive' : 'negative', {volume: 0.5});
            }
        }
    }
};
</script>

<style lang="scss" src="./Tinder.scss" />
